import React, { useEffect } from 'react';
import FiltersRow from './FiltersRow/FiltersRow';
import TableHead from './TableHead';
import VirtualTable from './VirtualTable';

import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import CodeRow from './CodesList/CodeListItems/CodeRow';
import { actions } from '../../../../../../context';

const TABLE_HEADER_HEIGHT = 100;

const CodesTable = (review) => {
    const dispatch = useDispatch();

    // TODO: do proper diffing
    const displayUsageDetails = useSelector(
        (state) => state.daylight.userHasAccessToCodeUsageDetails,
        shallowEqual
    );
    const daylightCodes = useSelector(
        (state) => state.daylight.daylightCodes,
        shallowEqual
    );
    const codeNameFilter = useSelector(
        (state) => state.daylight.codeNameFilter
    );
    const codeCategoryFilter = useSelector(
        (state) => state.daylight.codeCategoryFilter
    );
    const codeDescriptionFilter = useSelector(
        (state) => state.daylight.codeDescriptionFilter
    );
    const codeLocationFilter = useSelector(
        (state) => state.daylight.codeLocationFilter
    );
    const codeMappingFilter = useSelector(
        (state) => state.daylight.codeMappingFilter
    );
    const codeHasCommentsFilter = useSelector(
        (state) => state.daylight.codeHasCommentsFilter
    );
    const codeIsMarkedCompletedFilter = useSelector(
        (state) => state.daylight.codeIsMarkedCompletedFilter
    );
    const codeNeedsReviewFilter = useSelector(
        (state) => state.daylight.codeNeedsReviewFilter
    );
    const codeMarkedDisabledFilter = useSelector(
        (state) => state.daylight.codeMarkedDisabledFilter
    );

    const codeFilterLogic = (code) =>
        filterCodeName(code.code, codeNameFilter) &&
        filterCodeCategory(code.codeCategory, codeCategoryFilter) &&
        filterCodeLocation(code.locationId, codeLocationFilter) &&
        filterCodeMappings(code.mappings, codeMappingFilter) &&
        filterCodeDescription(code.codeDescription, codeDescriptionFilter) &&
        filterHasComments(code.mappings, codeHasCommentsFilter) &&
        filterIsMarkedCompleted(
            code.confirmationStatus,
            codeIsMarkedCompletedFilter
        ) &&
        filterNeedsReview(code.needsReview, codeNeedsReviewFilter) &&
        filterMarkedDisabled(code.markedDisabled, codeMarkedDisabledFilter);

    const filteredCodes = daylightCodes.filter(codeFilterLogic);

    useEffect(() => {
        dispatch(actions.daylight.setDisplayedCodesCount(filteredCodes.length));
    }, [filteredCodes, dispatch]);

    const displayCodeLabel = (index, vals = []) =>
        index === 0 || (index % 5 === 0 && index < vals.length - 1);

    const selectManyCodeIndexRowHeights = (codes, codeIndex = null, arr = []) => {
        codes.forEach((e, index) => {
            if (e && e.mappings && e.mappings.length) {
                // let isDisplayLabel = displayCodeLabel(index, e.mappings)
                arr = selectManyCodeIndexRowHeights(e.mappings, index, arr);
            }
            else {
                let isDisplayLabel = displayCodeLabel(index, codes)
                arr.push({
                    mappingIndex: index,
                    codeIndex: codeIndex ?? index,
                    rowHeight: isDisplayLabel ? 70 : 40
                });
            }
        });
    
        return arr;
    }
    const codeIndexRowHeights = selectManyCodeIndexRowHeights(filteredCodes);

    const getItemSize = index => codeIndexRowHeights[index].rowHeight || 40;

    /** The Row component. This should be a table row, and noted that we don't use the style that regular `react-window` examples pass in. */
    function Row({ index }) {
        let codeIndex = codeIndexRowHeights[index].codeIndex;
        let mappingIndex = codeIndexRowHeights[index].mappingIndex;
        return (
            <CodeRow
                key={index}
                code={filteredCodes[codeIndex]}
                codeIndex={codeIndex}
                mappingIndex={mappingIndex}
            />
        );
    }

    return (
        <div
            className="codes-table"
            style={{
                ['--table-header-height']: `${TABLE_HEADER_HEIGHT}px`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <div className="table-responsive vt-header" style={{ height: 'var(--table-header-height)', minHeight: 'var(--table-header-height)', flexShrink: 0 }}>
                <table style={{ minHeight: '100px' }} className="fixed-table w-100 bg-light text-dark sticky-top border-bottom text-12">
                    <TableHead/>
                </table>
            </div>
            <div
                className="table-responsive vt-body"
                style={{ height: 'calc(100% - var(--table-header-height))', minHeight: 0, flex: 1, overflow: 'hidden' }}
            >
                <VirtualTable
                    width="100%"
                    itemCount={codeIndexRowHeights.length}
                    codeCount={filteredCodes.length}
                    itemSize={getItemSize}
                    row={Row}
                    footer={
                        <tfoot>
                            {/* <div className="text-center p-3 op-7">
                                End of results {review && <>to review</>}
                            </div> */}
                        </tfoot>
                    }
                />
            </div>
        </div>
    )
};

const filterCodeName = (code, codeNameFilter) => {
    if (codeNameFilter === '') return true;
    const normalizedCode = code?.toLowerCase();
    const normalizedFilter = codeNameFilter?.toLowerCase();
    return normalizedCode?.includes(normalizedFilter);
};
const filterCodeCategory = (codeCategory, codeCategoryFilter) => {
    // TODO: does this to be normalized as well?
    if (codeCategoryFilter === '') return true;
    return codeCategory === codeCategoryFilter;
};
const filterCodeLocation = (locationId, codeLocationFilter) => {
    // TODO: does this need normalization too?
    if (codeLocationFilter === '') return true;
    return locationId === codeLocationFilter;
};
const filterTaxonomyLabel = (taxonomyCodeNodeLabel, codeMappingFilter) => {
    const normalizedTaxonomy = taxonomyCodeNodeLabel?.toLowerCase();
    return normalizedTaxonomy?.includes(codeMappingFilter);
};
const filterMappingCodeName = (codeName, codeMappingFilter) => {
    if (!codeName) return false;
    const normalizedCodeName = codeName?.toLowerCase();
    return normalizedCodeName.includes(codeMappingFilter);
};
const filterCodeMappings = (mappings, codeMappingFilter) => {
    if (codeMappingFilter === '') return true;
    const normalizedFilter = codeMappingFilter?.toLowerCase();
    const filteredMappings = mappings?.filter((mapping) => {
        const filteredTaxonomy = filterTaxonomyLabel(
            mapping.taxonomyCodeNodeLabel,
            normalizedFilter
        );
        const filteredCodeName = filterMappingCodeName(
            mapping.codeName,
            normalizedFilter
        );
        return filteredTaxonomy || filteredCodeName;
    });
    return filteredMappings?.length > 0;
};
const filterCodeDescription = (codeDescription, codeDescriptionFilter) => {
    if (codeDescriptionFilter === '') return true;
    const normalizedDescription = codeDescription?.toLowerCase();
    const normalizedDescriptionFilter = codeDescriptionFilter?.toLowerCase();
    return normalizedDescription?.includes(normalizedDescriptionFilter);
};
const filterHasComments = (codeMappings, codeHasCommentsFilter) => {
    if (codeHasCommentsFilter === null) return true;

    if (codeHasCommentsFilter) {
        return codeMappings?.some(
            (mapping) =>
                mapping.comments !== null && mapping.comments?.length > 0
        );
    } else {
        return codeMappings?.every(
            (mapping) =>
                mapping.comments === null || mapping.comments?.length === 0
        );
    }
};
const filterIsMarkedCompleted = (
    confirmationStatus,
    codeIsMarkedCompletedFilter
) => {
    if (codeIsMarkedCompletedFilter === null) return true;

    if (codeIsMarkedCompletedFilter) {
        return confirmationStatus === 1;
    } else {
        return confirmationStatus === 0;
    }
};

const filterNeedsReview = (needsReview, codeNeedsReviewFilter) => {
    if (codeNeedsReviewFilter === null) return true;

    if (codeNeedsReviewFilter) {
        return needsReview;
    } else {
        return !needsReview;
    }
};

const filterMarkedDisabled = (markedDisabled, codeMarkedDisabledFilter) => {
    if (codeMarkedDisabledFilter === null) return true;

    if (codeMarkedDisabledFilter) {
        return markedDisabled;
    } else {
        return !markedDisabled;
    }
};

export default CodesTable;